import questionnaire from '../config/questionnaire';

const STOP_WORDS = new Set([
  'de', 'la', 'que', 'el', 'en', 'y', 'a', 'los', 'del', 'se', 'las', 'por', 'un', 'para', 'con', 'no', 'una', 'su', 'al', 'lo', 'como',
  'más', 'mas', 'pero', 'sus', 'le', 'ya', 'o', 'este', 'ha', 'han', 'sí', 'si', 'porque', 'esta', 'son', 'entre', 'cuando', 'muy', 'sin',
  'sobre', 'también', 'tambien', 'me', 'hasta', 'hay', 'donde', 'quien', 'desde', 'todo', 'nos', 'durante', 'todos', 'uno', 'les', 'ni',
  'contra', 'otros', 'ese', 'eso', 'ante', 'ellos', 'e', 'esto', 'mi', 'mis', 'mes', 'meses', 'vez', 'forma', 'caso',
]);

const THEME_RULES = [
  {
    id: 'costs_margin',
    title: 'Costos y márgenes',
    summary: 'Presión económica asociada a fletes, recargos, tarifas o rentabilidad.',
    action: 'Cruzar recargos, justificación tarifaria y margen por naviera y ruta.',
    tone: 'rose',
    match: /\b(costo|costos|tarifa|tarifas|recargo|recargos|cobro|cobros|flete|fletes|margen|margenes|rentabilidad|precio|precios|incremento|aumento|caro|sobrecosto)\b/,
  },
  {
    id: 'availability_capacity',
    title: 'Cupos, espacio y contenedores',
    summary: 'Restricciones de disponibilidad, equipo, cupos o anticipación de reservas.',
    action: 'Medir disponibilidad real por naviera, tipo de equipo y ventana de reserva.',
    tone: 'amber',
    match: /\b(contenedor|contenedores|espacio|espacios|cupo|cupos|equipo|equipos|disponibilidad|reservar|reserva|booking|roll over|rollover|falta)\b/,
  },
  {
    id: 'schedule_reliability',
    title: 'Itinerarios y cumplimiento',
    summary: 'Señales sobre retrasos, blank sailings, cambios de ETA o confiabilidad.',
    action: 'Separar puntualidad, cambios de itinerario y días de impacto por naviera.',
    tone: 'sky',
    match: /\b(retraso|retrasos|demora|demoras|itinerario|itinerarios|eta|blank|sailing|salida|arribo|cumplimiento|puntualidad|transito|tránsito)\b/,
  },
  {
    id: 'port_friction',
    title: 'Fricción portuaria',
    summary: 'Problemas vinculados a congestión, accesos, inspecciones o capacidad portuaria.',
    action: 'Identificar puerto, tipo de fricción y etapa donde ocurre el cuello de botella.',
    tone: 'indigo',
    match: /\b(puerto|puertos|portuaria|portuario|congestion|congestión|inspeccion|inspección|aduana|aduanero|acceso|infraestructura|terminal|patio)\b/,
  },
  {
    id: 'communication_service',
    title: 'Comunicación y servicio',
    summary: 'Alertas sobre respuesta, documentación, trazabilidad o gestión de reclamos.',
    action: 'Comparar tiempos de respuesta y resolución de reclamos por naviera.',
    tone: 'teal',
    match: /\b(comunicacion|comunicación|respuesta|responder|informacion|información|documento|documentacion|documentación|trazabilidad|tracking|reclamo|reclamos|solucion|solución|atencion|atención)\b/,
  },
  {
    id: 'external_context',
    title: 'Factores externos',
    summary: 'Referencias a coyunturas externas que afectan rutas, tiempos o costos.',
    action: 'Separar factores externos de desempeño atribuible a navieras o puertos.',
    tone: 'slate',
    match: /\b(geopolitico|geopolítico|conflicto|conflictos|clima|climatico|climático|canal|suez|panama|panamá|huelga|crisis|internacional)\b/,
  },
];

const normalizeText = (text = '') =>
  text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const tokenizeText = (text) => normalizeText(text)
  .replace(/[.,;:!?¿¡()"']/g, '')
  .split(/\s+/)
  .filter((word) => word.length > 3 && !STOP_WORDS.has(word));

const topTerms = (entries = []) => {
  const freq = {};
  entries.forEach((entry) => {
    tokenizeText(entry.text).forEach((word) => {
      freq[word] = (freq[word] || 0) + 1;
    });
  });

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 5);
};

const getDiagnosticCarrierName = (detail = {}) => {
  if (detail.name === 'Otra') return detail.otherName || 'Otra naviera';
  return detail.name || '';
};

const collectQualitativeEntries = (submissions = []) => {
  const openQuestions = questionnaire.getAllQuestions().filter((q) => q.type === 'open');

  return submissions.flatMap((submission) => {
    const data = submission.data || {};
    const openEntries = openQuestions
      .map((question) => ({
        id: question.id,
        label: question.text,
        text: data[question.id]?.toString().trim() || '',
        naviera: submission.navieraPrincipal || data.meta_naviera_principal || '',
      }))
      .filter((entry) => entry.text.length >= 12);

    const diagnosticEntries = Array.isArray(data.meta_naviera_detalle)
      ? data.meta_naviera_detalle
          .map((detail) => ({
            id: 'meta_naviera_detalle',
            label: 'Comentarios por naviera',
            text: detail.comment?.toString().trim() || '',
            naviera: getDiagnosticCarrierName(detail),
          }))
          .filter((entry) => entry.text.length >= 12)
      : [];

    return [...openEntries, ...diagnosticEntries];
  });
};

export const buildQualitativeInsights = (submissions = []) => {
  const entries = collectQualitativeEntries(submissions);
  if (!entries.length) return [];

  const totalEntries = entries.length;
  const buckets = THEME_RULES.map((theme) => ({ ...theme, entries: [] }));
  const otherBucket = {
    id: 'other_operational_signals',
    title: 'Otras señales operativas',
    summary: 'Comentarios abiertos no clasificados en los temas principales.',
    action: 'Revisar manualmente si el tema gana recurrencia en próximos cortes.',
    tone: 'slate',
    entries: [],
  };

  entries.forEach((entry) => {
    const normalized = normalizeText(entry.text);
    const matches = buckets.filter((theme) => theme.match.test(normalized));
    if (matches.length) {
      matches.forEach((theme) => theme.entries.push(entry));
    } else {
      otherBucket.entries.push(entry);
    }
  });

  return [...buckets, otherBucket]
    .filter((theme) => theme.entries.length > 0)
    .map((theme) => {
      const sourceQuestions = Array.from(new Set(theme.entries.map((entry) => entry.label))).slice(0, 3);
      const navieras = Array.from(new Set(theme.entries.map((entry) => entry.naviera).filter(Boolean))).slice(0, 4);
      const count = theme.entries.length;

      return {
        id: theme.id,
        title: theme.title,
        summary: theme.summary,
        action: theme.action,
        tone: theme.tone,
        count,
        share: Math.round((count / totalEntries) * 100),
        confidenceLabel: count >= 3 ? 'Recurrente' : count >= 2 ? 'Emergente' : 'Aislado',
        sourceQuestions,
        navieras,
        topWords: topTerms(theme.entries),
      };
    })
    .sort((a, b) => b.count - a.count || b.share - a.share)
    .slice(0, 4);
};
